// @flow

import gql from 'graphql-tag';
import { get, omit, omitBy } from 'lodash';

import {
  type PaymentSource,
  type PaymentSourceBackend,
  type PaymentSourceMethod,
  paymentSourceMethods,
} from '@zapier/payment';
import { useMutation, useQuery } from '@apollo/react-hooks';

type Input = {
  accountId: number,
  onError: (error: Error, method: PaymentSourceMethod) => void,
  onSuccess: ({ saveBankPaymentSource: PaymentSource }) => void,
};

type Output = {
  error: ?string,
  isLoading: boolean,
  isSaving: boolean,
  onSave: ({}, PaymentSourceMethod) => void,
  paymentBackend: ?PaymentSourceBackend,
  paymentSources: Array<PaymentSource>,
  primarySource: ?PaymentSource,
  supportedPaymentMethods: Array<PaymentSourceMethod>,
};

const paymentSourceFragment = gql`
  fragment paymentSource on PaymentSource {
    backend
    details {
      ... on PaymentSourceBank {
        accountHolderName
        accountType
        addressCity
        addressCountry
        addressState
        addressZip
        bankName
        last4
        ownershipType
        routingNumber
        verified
      }

      ... on PaymentSourceCard {
        addressCity
        addressCountry
        addressState
        addressZip
        brand
        expiry
        last4
        name
      }

      ... on PaymentSourcePaypal {
        email
      }
    }
    id
    type
  }
`;

const paymentMethodsQuery = gql`
  ${paymentSourceFragment}

  query paymentMethodsQuery($id: Int!) {
    account(id: $id) {
      id
      paymentBackend
      paymentSources {
        ...paymentSource
      }
      supportedPaymentMethods
    }
  }
`;

const saveMutation = {
  bank: gql`
    ${paymentSourceFragment}

    mutation SaveBankPaymentSource($accountId: Int!, $data: BankInput!) {
      saveBankPaymentSource(accountId: $accountId, data: $data) {
        ...paymentSource
      }
    }
  `,
  card: gql`
    ${paymentSourceFragment}

    mutation SaveCardPaymentSource($accountId: Int!, $data: CardInput!) {
      saveCardPaymentSource(accountId: $accountId, data: $data) {
        ...paymentSource
      }
    }
  `,
  paypal: gql`
    ${paymentSourceFragment}

    mutation SavePaypalPaymentSource($accountId: Int!, $data: PaypalInput!) {
      savePaypalPaymentSource(accountId: $accountId, data: $data) {
        ...paymentSource
      }
    }
  `,
};

const transformSaveDetails = (data: {}, method: PaymentSourceMethod) => {
  if (method === paymentSourceMethods.card) {
    return {
      ...data,
      // Removes all null properties from data these are returned
      // by the tokenization process and can be ignored, otherwise
      // graphql complains / also ignores metadata
      details: omit(
        omitBy(
          get(data, 'details', {}),
          val => val === null || val === undefined || val === ''
        ),
        ['metadata']
      ),
    };
  }

  return data;
};

const usePaymentSources = ({
  accountId,
  onError,
  onSuccess,
}: Input): Output => {
  const queryResults = useQuery(paymentMethodsQuery, {
    skip: !accountId,
    variables: {
      id: accountId,
    },
  });
  const [save, saveResults] = useMutation(saveMutation.card, {
    awaitRefetchQueries: true,
    // not sure why but onCompleted only works if it's defined here
    // and not later when the save is fire
    onCompleted: onSuccess,
    refetchQueries: () => [
      {
        query: paymentMethodsQuery,
        variables: {
          id: accountId,
        },
      },
    ],
  });

  const paymentSources = get(
    queryResults,
    ['data', 'account', 'paymentSources'],
    []
  );

  const onSave = (data: {}, method: PaymentSourceMethod) => {
    save({
      // there have to be different graphql mutations for each
      // payment source type because union input types aren't
      // available in graphql yet
      mutation: saveMutation[method],
      onError: error => onError(error, method),
      variables: {
        accountId,
        data: transformSaveDetails(data, method),
      },
    });
  };

  const error = queryResults.error
    ? 'There was an issue loading your payment sources. Please try again'
    : saveResults.error;

  return {
    error,
    isLoading: queryResults.loading,
    isSaving: saveResults.loading,
    onSave,
    paymentBackend: get(queryResults, ['data', 'account', 'paymentBackend']),
    paymentSources,
    primarySource: paymentSources[0],
    supportedPaymentMethods: get(
      queryResults,
      ['data', 'account', 'supportedPaymentMethods'],
      []
    ),
  };
};

export default usePaymentSources;
